import React, { useState, useEffect, useRef, useMemo } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import DataPackagesModal from './DataPackagesModal';
import DataPackageModal from './DataPackageModal';
import AccessDeniedModal from './AccessDeniedModal';
import ReliabilityModal from './ReliabilityModal';
import AumBucketFilter from './AumBucketFilter';
import EmployeeBucketFilter from './EmployeeBucketFilter';
import { ALL_AUM_BUCKET_IDS, aumMatchesSelectedBuckets, type AumBucketId } from './aumFilterBuckets';
import { ALL_EMPLOYEE_BUCKET_IDS, employeeMatchesSelectedBuckets, type EmployeeBucketId } from './employeeFilterBuckets';
import { getMarketDataByPackageName, MarketDataRecord } from '../../services/marketDataService';
import { parseOptionalFiniteNumber } from '../../utils/optionalNumber';
import {
  countRecordsWithoutMarketGroup,
  filterRecordsByMarketGroup,
  filterRecordsWithMarketGroup,
  getMarketGroupColumnLabel,
  getSortedMarketGroups,
  type MarketGroupingMode,
} from '../../utils/marketGroupUtils';
import { buildRoleClusterMetaList } from '../../utils/marketClusterMeta';
import { fetchCommunityGradingTreeCached } from '../../services/gradingService';
import { normalizeGradingTreePayload } from './admin/grading/utils';
import type { GradingTreePayload } from './admin/grading/types';
import MarketDashboardSectionHeader from './MarketDashboardSectionHeader';
import MarketGroupLabelCell from './MarketGroupLabelCell';
import { CustomSelect } from '../common/CustomSelect';
import { computeLtipFormStats, countRecordsWithLtipForm } from '../../utils/ltipFormStats';
import LtipFormDistributionChart from './LtipFormDistributionChart';
import {
  getAllPackagesWithAccessStatus,
  getUniqueSectors,
  normalizePackageDescription,
  DataPackage,
} from '../../services/dataPackagesService';

import type { WorkspaceTourPrepare } from './tour/workspaceTourConfig';

interface MarketSectionProps {
  initialPackageName?: string;
  initialPackageDisplayName?: string;
  onOpenPackage?: (packageName: string, displayName: string) => void;
  onBack?: () => void;
  tourPrepareKind?: WorkspaceTourPrepare | null;
  tourPrepareNonce?: number;
}

const SEARCH_PLACEHOLDER_FULL = 'Поиск по компенсационным пакетам...';

const MARKET_MONEY_TABLE_STYLE: React.CSSProperties = {
  width: 'max-content',
  minWidth: '100%',
  borderCollapse: 'collapse',
  fontSize: '14px',
  tableLayout: 'auto',
};

const MARKET_PERCENTILE_CELL_STYLE: React.CSSProperties = {
  padding: '12px 12px',
  textAlign: 'center',
  fontSize: '11px',
  whiteSpace: 'nowrap',
};

const MARKET_PERCENTILE_HEADER_STYLE: React.CSSProperties = {
  padding: '16px 12px',
  textAlign: 'center',
  fontWeight: '600',
  fontSize: '16px',
  borderRight: '1px solid rgba(255, 255, 255, 0.2)',
  minWidth: '132px',
};

const MARKET_PERCENTILE_HEADER_LAST_STYLE: React.CSSProperties = {
  padding: '16px 12px',
  textAlign: 'center',
  fontWeight: '600',
  fontSize: '16px',
  minWidth: '132px',
};

const RELIABILITY_LEVELS = [
  { id: 'Excellent' as const, label: 'Очень высокая (Excellent)', min: 0.8, max: 1, color: '#073763' },
  { id: 'High' as const, label: 'Высокая (High)', min: 0.5, max: 0.8, color: '#1155cc' },
  { id: 'Moderate' as const, label: 'Умеренная (Moderate)', min: 0.3, max: 0.5, color: '#6d9eeb' },
  { id: 'Low' as const, label: 'Низкая (Low)', min: 0, max: 0.3, color: '#999999' },
] as const;

type ReliabilityLevelId = typeof RELIABILITY_LEVELS[number]['id'];

function getReliabilityLevelForScore(score: number): ReliabilityLevelId {
  if (score > 0.8) return 'Excellent';
  if (score > 0.5) return 'High';
  if (score > 0.3) return 'Moderate';
  return 'Low';
}

type CompanyItem = { id: string; name: string };

type MarketDashboardId = 'fix' | 'sti' | 'total' | 'ltip' | 'ltipForm';

const DEFAULT_DASHBOARD_GROUPING: Record<MarketDashboardId, MarketGroupingMode> = {
  fix: 'grade',
  sti: 'grade',
  total: 'grade',
  ltip: 'grade',
  ltipForm: 'grade',
};

const MarketSection: React.FC<MarketSectionProps> = ({
  initialPackageName,
  initialPackageDisplayName,
  onOpenPackage,
  onBack,
  tourPrepareKind,
  tourPrepareNonce = 0,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [marketData, setMarketData] = useState<MarketDataRecord[]>([]);
  const [selectedPackageDisplayName, setSelectedPackageDisplayName] = useState<string>('');
  const [selectedPackageName, setSelectedPackageName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isReliabilityModalOpen, setIsReliabilityModalOpen] = useState(false);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [filteredData, setFilteredData] = useState<MarketDataRecord[]>([]);
  const [typewriterText, setTypewriterText] = useState('');
  const [filterMode, setFilterMode] = useState<'sector' | 'company'>('sector');
  const [selectedReliabilityLevels, setSelectedReliabilityLevels] = useState<ReliabilityLevelId[]>(['Excellent', 'High', 'Moderate', 'Low']);
  const [selectedAumBuckets, setSelectedAumBuckets] = useState<AumBucketId[]>([...ALL_AUM_BUCKET_IDS]);
  const [selectedEmployeeBuckets, setSelectedEmployeeBuckets] = useState<EmployeeBucketId[]>([...ALL_EMPLOYEE_BUCKET_IDS]);
  const [isExportingXlsx, setIsExportingXlsx] = useState(false);
  const [exportXlsxError, setExportXlsxError] = useState<string>('');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [expandedCompanySectors, setExpandedCompanySectors] = useState<string[]>([]);
  const [showTotalCompTooltip, setShowTotalCompTooltip] = useState(false);
  const [showStiTooltip, setShowStiTooltip] = useState(false);
  const [hoveredFilterMetric, setHoveredFilterMetric] = useState<string | null>(null);
  const [dashboardGrouping, setDashboardGrouping] = useState(DEFAULT_DASHBOARD_GROUPING);
  const [gradingTree, setGradingTree] = useState<GradingTreePayload | null>(null);
  const [selectedIndustryId, setSelectedIndustryId] = useState<string>(() => {
    return localStorage.getItem('workspace_industry_id') || '';
  });
  const [accessDeniedModal, setAccessDeniedModal] = useState<{
    isOpen: boolean;
    packageName: string;
    dataPackageId?: string;
  }>({
    isOpen: false,
    packageName: '',
  });

  // Каталог пакетов
  const [catalogPackages, setCatalogPackages] = useState<(DataPackage & { hasAccess: boolean })[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const didAutoSelectAccessibleIndustry = useRef(false);

  useEffect(() => {
    if (tourPrepareKind === 'market-filters' && tourPrepareNonce > 0) {
      setIsFiltersExpanded(true);
    }
  }, [tourPrepareKind, tourPrepareNonce]);

  const loadCatalogPackages = React.useCallback(async () => {
    setCatalogLoading(true);
    try {
      const data = await getAllPackagesWithAccessStatus();
      setCatalogPackages(data);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  // Модальное окно "Подробнее"
  const [detailPackage, setDetailPackage] = useState<DataPackage | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const getDatacircleLogoPngDataUrl = async (): Promise<string> => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256">
        <defs>
          <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color="#1a237e"/>
            <stop offset="1" stop-color="#283593"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="256" height="256" rx="36" fill="url(#bg)"/>
        <g transform="translate(70 62)" fill="#ffffff">
          <rect x="0" y="72" width="28" height="64" rx="8"/>
          <rect x="42" y="32" width="28" height="104" rx="8"/>
          <rect x="84" y="0" width="28" height="136" rx="8"/>
        </g>
      </svg>
    `.trim();

    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Не удалось загрузить SVG логотип'));
        image.src = svgUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas не поддерживается');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  };

  const formatDateForFilename = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}_${hh}-${mi}`;
  };

  const handleExportXlsx = async () => {
    if (!selectedPackageDisplayName) return;
    if (isExportingXlsx) return;
    setExportXlsxError('');

    if (filteredData.length === 0) {
      setExportXlsxError('Нет данных для экспорта: попробуйте изменить фильтры.');
      return;
    }

    setIsExportingXlsx(true);
    try {
      const exportGroupingMode = (dashboardId: MarketDashboardId): MarketGroupingMode => {
        const mode = dashboardGrouping[dashboardId];
        return mode === 'cluster' && clusterMetas.length > 0 ? 'cluster' : 'grade';
      };
      const exportMarketGroups = (dashboardId: MarketDashboardId) =>
        getSortedMarketGroups(filteredData, exportGroupingMode(dashboardId), clusterMetas);
      const exportGroupRecords = (dashboardId: MarketDashboardId, groupKey: string) =>
        filterRecordsByMarketGroup(filteredData, groupKey, exportGroupingMode(dashboardId), clusterMetas);

      const percentiles = [10, 25, 50, 75, 90] as const;

      const calculatePercentile = (data: number[], percentile: number): number => {
        if (data.length === 0) return 0;
        const sorted = [...data].sort((a, b) => a - b);
        const index = Math.ceil((percentile / 100) * sorted.length) - 1;
        return sorted[Math.max(0, index)];
      };

      const getAllowedPercentiles = (n: number, ps: readonly number[]) => {
        const seenIndexes = new Set<number>();
        const allowed = new Set<number>();
        for (const p of ps) {
          const idx = Math.ceil((p / 100) * n) - 1;
          if (!seenIndexes.has(idx)) {
            seenIndexes.add(idx);
            allowed.add(p);
          }
        }
        return allowed;
      };

      const getReliabilityShortText = (avgScore: number) => {
        if (avgScore > 0.8) return 'Excellent';
        if (avgScore > 0.5) return 'High';
        if (avgScore > 0.3) return 'Moderate';
        return 'Low';
      };

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'datacircle';
      workbook.created = new Date();

      const sheet = workbook.addWorksheet('Отчет');
      sheet.properties.defaultRowHeight = 18;
      sheet.views = [{ state: 'frozen', ySplit: 6 }];

      const logoDataUrl = await getDatacircleLogoPngDataUrl();
      const logoId = workbook.addImage({ base64: logoDataUrl, extension: 'png' });
      // Header layout: logo left, package title centered
      sheet.addImage(logoId, { tl: { col: 0, row: 0 }, ext: { width: 54, height: 54 } });
      sheet.getRow(1).height = 42;
      sheet.getRow(2).height = 20;
      sheet.getRow(3).height = 18;
      sheet.getRow(4).height = 6;

      // We'll use 9 columns for tables (Среднее после 90p)
      sheet.columns = [
        { width: 30 }, // A: family + grade
        { width: 18 }, // B: reliability
        { width: 16 }, // C: count
        { width: 14 }, // D: 10p
        { width: 14 }, // E: 25p
        { width: 14 }, // F: 50p
        { width: 14 }, // G: 75p
        { width: 14 }, // H: 90p
        { width: 14 }, // I: Среднее
      ];

      sheet.mergeCells(1, 2, 1, 9);
      sheet.getCell(1, 2).value = selectedPackageDisplayName;
      sheet.getCell(1, 2).font = { name: 'Poppins', size: 18, bold: true, color: { argb: 'FF1A237E' } };
      sheet.getCell(1, 2).alignment = { vertical: 'middle', horizontal: 'center' };

      sheet.mergeCells(2, 2, 2, 9);
      sheet.getCell(2, 2).value = 'Отчет';
      sheet.getCell(2, 2).font = { name: 'Poppins', size: 12, bold: true, color: { argb: 'FF333333' } };
      sheet.getCell(2, 2).alignment = { vertical: 'middle', horizontal: 'center' };

      sheet.mergeCells(3, 1, 3, 9);
      sheet.getCell(3, 1).value =
        `Выбрано в фильтрах: режим=${filterMode === 'sector' ? 'по секторам' : 'по компаниям'}, ` +
        `секторов=${selectedSectors.length}, компаний=${selectedCompanies.length}, ` +
        `надежность=${selectedReliabilityLevels.join(', ') || '—'}`;
      sheet.getCell(3, 1).font = { name: 'Poppins', size: 10, color: { argb: 'FF555555' } };
      sheet.getCell(3, 1).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };

      const applyTableHeaderStyle = (rowIdx: number, fromCol: number, toCol: number) => {
        const row = sheet.getRow(rowIdx);
        row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        row.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        row.height = 22;
        for (let c = fromCol; c <= toCol; c += 1) {
          const cell = row.getCell(c);
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A237E' } };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFDDDDDD' } },
            left: { style: 'thin', color: { argb: 'FFDDDDDD' } },
            bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } },
            right: { style: 'thin', color: { argb: 'FFDDDDDD' } },
          };
        }
      };

      const applyTableRowStyle = (rowIdx: number, fromCol: number, toCol: number) => {
        const row = sheet.getRow(rowIdx);
        row.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        for (let c = fromCol; c <= toCol; c += 1) {
          const cell = row.getCell(c);
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFEEEEEE' } },
            left: { style: 'thin', color: { argb: 'FFEEEEEE' } },
            bottom: { style: 'thin', color: { argb: 'FFEEEEEE' } },
            right: { style: 'thin', color: { argb: 'FFEEEEEE' } },
          };
        }
        row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      };

      const writeSectionTitle = (rowIdx: number, title: string) => {
        sheet.mergeCells(rowIdx, 1, rowIdx, 9);
        const cell = sheet.getCell(rowIdx, 1);
        cell.value = title;
        cell.font = { name: 'Poppins', size: 14, bold: true, color: { argb: 'FF1A237E' } };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        sheet.getRow(rowIdx).height = 22;
      };

      let currentRow = 6;

      // 1) FIX table (same columns as UI)
      writeSectionTitle(currentRow, 'Фиксированное вознаграждение (месяц, GROSS)');
      currentRow += 1;
      const fixHeaderRow = currentRow;
      sheet.getRow(fixHeaderRow).values = [
        getMarketGroupColumnLabel(exportGroupingMode('fix')),
        'Надежность данных',
        'Кол-во датакейсов',
        '10p',
        '25p',
        '50p',
        '75p',
        '90p',
        'Среднее',
      ];
      applyTableHeaderStyle(fixHeaderRow, 1, 9);
      currentRow += 1;

      exportMarketGroups('fix').forEach((group) => {
        const positionData = exportGroupRecords('fix', group.key);
        const fixValues = positionData.map((r) => r.fix);
        const reliabilityValues = positionData.map((r) => r.reliability_score);
        const allowed = getAllowedPercentiles(fixValues.length, percentiles);
        const avgReliability =
          reliabilityValues.length > 0 ? reliabilityValues.reduce((sum, val) => sum + val, 0) / reliabilityValues.length : 0;
        const reliabilityShort = getReliabilityShortText(avgReliability);

        const uniqueCompanyCountFix = new Set(positionData.map((r) => r.company_id)).size;
        const p10eligibleFix = allowed.has(10) && uniqueCompanyCountFix >= 10 && positionData.length >= 30;
        const p25eligibleFix = allowed.has(25) && uniqueCompanyCountFix >= 7 && positionData.length >= 30;
        const p50eligibleFix = allowed.has(50) && uniqueCompanyCountFix >= 5 && positionData.length >= 10;
        const avgEligibleFix = uniqueCompanyCountFix >= 3 && positionData.length >= 5;
        const p75eligibleFix = allowed.has(75) && uniqueCompanyCountFix >= 7 && positionData.length >= 30;
        const p90eligibleFix = allowed.has(90) && uniqueCompanyCountFix >= 10 && positionData.length >= 30;
        const p10 = p10eligibleFix ? calculatePercentile(fixValues, 10) : '-';
        const p25 = p25eligibleFix ? calculatePercentile(fixValues, 25) : '-';
        const p50 = p50eligibleFix ? calculatePercentile(fixValues, 50) : '-';
        const avgFix = avgEligibleFix ? Math.round(fixValues.reduce((s, v) => s + v, 0) / fixValues.length) : '-';
        const p75 = p75eligibleFix ? calculatePercentile(fixValues, 75) : '-';
        const p90 = p90eligibleFix ? calculatePercentile(fixValues, 90) : '-';

        sheet.getRow(currentRow).values = [group.label, reliabilityShort, positionData.length, p10, p25, p50, p75, p90, avgFix];
        applyTableRowStyle(currentRow, 1, 9);
        for (let c = 4; c <= 9; c += 1) {
          sheet.getRow(currentRow).getCell(c).numFmt = '#,##0" ₽"';
        }
        currentRow += 1;
      });

      currentRow += 2;

      // 2) STI table (same columns as UI; UI prints (p*100).toFixed(1)%)
      writeSectionTitle(currentRow, 'Нацеленное краткосрочное переменное вознаграждение (Target STI)');
      currentRow += 1;
      const stiHeaderRow = currentRow;
      sheet.getRow(stiHeaderRow).values = [
        getMarketGroupColumnLabel(exportGroupingMode('sti')),
        'Надежность данных',
        'Кол-во датакейсов',
        '10p',
        '25p',
        '50p',
        '75p',
        '90p',
        'Среднее',
      ];
      applyTableHeaderStyle(stiHeaderRow, 1, 9);
      currentRow += 1;

      exportMarketGroups('sti').forEach((group) => {
        const positionData = exportGroupRecords('sti', group.key);
        const stiValues = positionData.map((r) => r.sti);
        const reliabilityValues = positionData.map((r) => r.reliability_score);
        const allowed = getAllowedPercentiles(stiValues.length, percentiles);
        const avgReliability =
          reliabilityValues.length > 0 ? reliabilityValues.reduce((sum, val) => sum + val, 0) / reliabilityValues.length : 0;
        const reliabilityShort = getReliabilityShortText(avgReliability);

        const uniqueCompanyCountSti = new Set(positionData.map((r) => r.company_id)).size;
        const p10eligibleSti = allowed.has(10) && uniqueCompanyCountSti >= 10 && positionData.length >= 30;
        const p25eligibleSti = allowed.has(25) && uniqueCompanyCountSti >= 7 && positionData.length >= 30;
        const p50eligibleSti = allowed.has(50) && uniqueCompanyCountSti >= 5 && positionData.length >= 10;
        const avgEligibleSti = uniqueCompanyCountSti >= 3 && positionData.length >= 5;
        const p75eligibleSti = allowed.has(75) && uniqueCompanyCountSti >= 7 && positionData.length >= 30;
        const p90eligibleSti = allowed.has(90) && uniqueCompanyCountSti >= 10 && positionData.length >= 30;
        const p10 = p10eligibleSti ? calculatePercentile(stiValues, 10) * 100 : '-';
        const p25 = p25eligibleSti ? calculatePercentile(stiValues, 25) * 100 : '-';
        const p50 = p50eligibleSti ? calculatePercentile(stiValues, 50) * 100 : '-';
        const avgSti = avgEligibleSti ? +(stiValues.reduce((s, v) => s + v, 0) / stiValues.length * 100).toFixed(1) : '-';
        const p75 = p75eligibleSti ? calculatePercentile(stiValues, 75) * 100 : '-';
        const p90 = p90eligibleSti ? calculatePercentile(stiValues, 90) * 100 : '-';

        sheet.getRow(currentRow).values = [group.label, reliabilityShort, positionData.length, p10, p25, p50, p75, p90, avgSti];
        applyTableRowStyle(currentRow, 1, 9);
        for (let c = 4; c <= 9; c += 1) {
          sheet.getRow(currentRow).getCell(c).numFmt = '0.0"%"';
        }
        currentRow += 1;
      });

      currentRow += 2;

      // 3) Total compensation table (year, RUB; LTIP excluded)
      writeSectionTitle(currentRow, 'Нацеленное совокупное вознаграждение (Total cash at target)');
      currentRow += 1;
      const totalHeaderRow = currentRow;
      sheet.getRow(totalHeaderRow).values = [
        getMarketGroupColumnLabel(exportGroupingMode('total')),
        'Надежность данных',
        'Кол-во датакейсов',
        '10p',
        '25p',
        '50p',
        '75p',
        '90p',
        'Среднее',
      ];
      applyTableHeaderStyle(totalHeaderRow, 1, 9);
      currentRow += 1;

      exportMarketGroups('total').forEach((group) => {
        const positionData = exportGroupRecords('total', group.key);
        const totalValues = positionData.map((r) => (r.fix * 12) * (1 + r.sti));
        const reliabilityValues = positionData.map((r) => r.reliability_score);
        const allowed = getAllowedPercentiles(totalValues.length, percentiles);
        const avgReliability =
          reliabilityValues.length > 0 ? reliabilityValues.reduce((sum, val) => sum + val, 0) / reliabilityValues.length : 0;
        const reliabilityShort = getReliabilityShortText(avgReliability);

        const uniqueCompanyCountTotal = new Set(positionData.map((r) => r.company_id)).size;
        const p10eligibleTotal = allowed.has(10) && uniqueCompanyCountTotal >= 10 && positionData.length >= 30;
        const p25eligibleTotal = allowed.has(25) && uniqueCompanyCountTotal >= 7 && positionData.length >= 30;
        const p50eligibleTotal = allowed.has(50) && uniqueCompanyCountTotal >= 5 && positionData.length >= 10;
        const avgEligibleTotal = uniqueCompanyCountTotal >= 3 && positionData.length >= 5;
        const p75eligibleTotal = allowed.has(75) && uniqueCompanyCountTotal >= 7 && positionData.length >= 30;
        const p90eligibleTotal = allowed.has(90) && uniqueCompanyCountTotal >= 10 && positionData.length >= 30;
        const p10 = p10eligibleTotal ? calculatePercentile(totalValues, 10) : '-';
        const p25 = p25eligibleTotal ? calculatePercentile(totalValues, 25) : '-';
        const p50 = p50eligibleTotal ? calculatePercentile(totalValues, 50) : '-';
        const avgTotal = avgEligibleTotal ? Math.round(totalValues.reduce((s, v) => s + v, 0) / totalValues.length) : '-';
        const p75 = p75eligibleTotal ? calculatePercentile(totalValues, 75) : '-';
        const p90 = p90eligibleTotal ? calculatePercentile(totalValues, 90) : '-';

        sheet.getRow(currentRow).values = [group.label, reliabilityShort, positionData.length, p10, p25, p50, p75, p90, avgTotal];
        applyTableRowStyle(currentRow, 1, 9);
        for (let c = 4; c <= 9; c += 1) {
          sheet.getRow(currentRow).getCell(c).numFmt = '#,##0" ₽"';
        }
        currentRow += 1;
      });

      currentRow += 2;

      // 4) LTIP table (same columns as UI)
      writeSectionTitle(currentRow, 'Долгосрочное вознаграждение (LTI)');
      currentRow += 1;
      const ltipHeaderRow = currentRow;
      sheet.getRow(ltipHeaderRow).values = [
        getMarketGroupColumnLabel(exportGroupingMode('ltip')),
        'Надежность данных',
        'Кол-во датакейсов',
        'Да / Нет',
      ];
      applyTableHeaderStyle(ltipHeaderRow, 1, 4);
      currentRow += 1;

      exportMarketGroups('ltip').forEach((group) => {
        const positionData = exportGroupRecords('ltip', group.key);
        const ltipYesCount = positionData.filter((r) => r.ltip && r.ltip.toLowerCase() === 'да').length;
        const ltipNoCount = positionData.filter((r) => !r.ltip || r.ltip.toLowerCase() === 'нет').length;
        const total = ltipYesCount + ltipNoCount;
        const yesPct = total > 0 ? (ltipYesCount / total) * 100 : 0;
        const noPct = total > 0 ? (ltipNoCount / total) * 100 : 0;

        const reliabilityValues = positionData.map((r) => r.reliability_score);
        const avgReliability =
          reliabilityValues.length > 0 ? reliabilityValues.reduce((sum, val) => sum + val, 0) / reliabilityValues.length : 0;
        const reliabilityShort = getReliabilityShortText(avgReliability);

        sheet.getRow(currentRow).values = [
          group.label,
          reliabilityShort,
          positionData.length,
          `Есть: ${yesPct.toFixed(0)}% / Нет: ${noPct.toFixed(0)}%`,
        ];
        applyTableRowStyle(currentRow, 1, 4);
        sheet.getRow(currentRow).getCell(4).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        currentRow += 1;
      });

      // Autofit header area a bit
      sheet.getRow(5).height = 6;

      const [buffer] = await Promise.all([
        workbook.xlsx.writeBuffer(),
        new Promise((resolve) => setTimeout(resolve, 2300)),
      ]);
      const fileName = `datacircle_${selectedPackageDisplayName.replace(/[\\/:*?"<>|]+/g, '_')}_${formatDateForFilename(new Date())}.xlsx`;
      saveAs(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        fileName
      );
    } catch (e) {
      console.error('Ошибка генерации xlsx:', e);
      setExportXlsxError('Не удалось сформировать отчет. Попробуйте еще раз.');
    } finally {
      setIsExportingXlsx(false);
    }
  };

  // Загружаем каталог пакетов
  useEffect(() => {
    void loadCatalogPackages();
  }, [loadCatalogPackages]);

  const accessiblePackages = useMemo(
    () => catalogPackages.filter((pkg) => pkg.package_name && pkg.hasAccess),
    [catalogPackages],
  );

  useEffect(() => {
    if (catalogLoading) return;

    if (!selectedIndustryId && accessiblePackages.length > 0) {
      const first = accessiblePackages[0];
      if (first?.package_name) {
        setSelectedIndustryId(first.package_name);
        localStorage.setItem('workspace_industry_id', first.package_name);
      }
      return;
    }

    if (!didAutoSelectAccessibleIndustry.current && selectedIndustryId && accessiblePackages.length > 0) {
      const selectedPkg = catalogPackages.find(
        (pkg) => pkg.package_name === selectedIndustryId || pkg.id === selectedIndustryId,
      );
      if (selectedPkg && !selectedPkg.hasAccess) {
        const first = accessiblePackages[0];
        if (first?.package_name) {
          setSelectedIndustryId(first.package_name);
          localStorage.setItem('workspace_industry_id', first.package_name);
        }
      }
      didAutoSelectAccessibleIndustry.current = true;
    }
  }, [accessiblePackages, catalogLoading, catalogPackages, selectedIndustryId]);

  // Если передан initialPackageName извне (из вкладки Данные) — сразу открываем
  useEffect(() => {
    if (initialPackageName && initialPackageDisplayName) {
      handlePackageSelect(initialPackageName, initialPackageDisplayName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPackageName, initialPackageDisplayName]);

  const handleSearchClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const industryOptions = React.useMemo(
    () =>
      catalogPackages
        .filter((pkg) => pkg.package_name)
        .map((pkg) => ({
          id: pkg.package_name as string,
          label: pkg.hasAccess ? pkg.name : `${pkg.name} (нет доступа)`,
        })),
    [catalogPackages]
  );

  const selectedCatalogPackage = React.useMemo(
    () =>
      catalogPackages.find(
        (pkg) => pkg.package_name === selectedIndustryId || pkg.id === selectedIndustryId
      ) ?? null,
    [catalogPackages, selectedIndustryId]
  );

  const isSelectedPackageAccessDenied = Boolean(
    selectedIndustryId && !catalogLoading && selectedCatalogPackage && !selectedCatalogPackage.hasAccess
  );

  const handleIndustryChange = (id: string) => {
    setSelectedIndustryId(id);
    if (id) {
      localStorage.setItem('workspace_industry_id', id);
    } else {
      localStorage.removeItem('workspace_industry_id');
    }
    setSelectedPackageDisplayName('');
    setSelectedPackageName('');
    setMarketData([]);
    setFilteredData([]);
    setSelectedSectors([]);
    setSelectedCompanies([]);
    setIsFiltersExpanded(false);
    setExpandedCompanySectors([]);
    if (onBack) onBack();
  };

  const handlePackageSelect = async (packageName: string, packageDisplayName: string) => {
    setLoading(true);
    setSelectedPackageDisplayName(packageDisplayName);
    setSelectedPackageName(packageName);
    setIsFiltersExpanded(false);
    setExpandedCompanySectors([]);
    
    try {
      const data = await getMarketDataByPackageName(packageName);
      setMarketData(data);
      setFilteredData(data);
      const uniqueSectors = Array.from(new Set(data.map(record => record.sector)));
      setSelectedSectors(uniqueSectors);
      setSelectedCompanies([]);
      setSelectedReliabilityLevels(['Excellent', 'High', 'Moderate', 'Low']);
      setSelectedAumBuckets([...ALL_AUM_BUCKET_IDS]);
      setSelectedEmployeeBuckets([...ALL_EMPLOYEE_BUCKET_IDS]);
    } catch (error) {
      console.error('Error loading market data:', error);
      setMarketData([]);
      setFilteredData([]);
      setSelectedSectors([]);
      setSelectedCompanies([]);
      setSelectedReliabilityLevels(['Excellent', 'High', 'Moderate', 'Low']);
      setSelectedAumBuckets([...ALL_AUM_BUCKET_IDS]);
      setSelectedEmployeeBuckets([...ALL_EMPLOYEE_BUCKET_IDS]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (catalogLoading || catalogPackages.length === 0 || !selectedIndustryId) return;
    const exists = catalogPackages.some(
      (pkg) => pkg.package_name === selectedIndustryId || pkg.id === selectedIndustryId
    );
    if (!exists) {
      setSelectedIndustryId('');
      localStorage.removeItem('workspace_industry_id');
    }
  }, [catalogLoading, catalogPackages, selectedIndustryId]);

  useEffect(() => {
    if (!selectedIndustryId || selectedPackageDisplayName || loading || catalogLoading) return;
    const pkg = catalogPackages.find(
      (p) => p.package_name === selectedIndustryId || p.id === selectedIndustryId
    );
    if (!pkg?.package_name || !pkg.hasAccess) return;
    void handlePackageSelect(pkg.package_name, pkg.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndustryId, selectedPackageDisplayName, loading, catalogLoading, catalogPackages]);

  // Фильтрация: по секторам или компаниям, затем дополнительно по надежности
  const applyFilters = () => {
    let data: MarketDataRecord[];
    if (filterMode === 'sector') {
      if (selectedSectors.length === 0) {
        setFilteredData([]);
        return;
      }
      data = marketData.filter(record =>
        selectedSectors.includes(record.sector)
      );
    } else {
      if (selectedCompanies.length === 0) {
        setFilteredData([]);
        return;
      }
      data = marketData.filter(record =>
        selectedCompanies.includes(record.company_id)
      );
    }
    if (selectedReliabilityLevels.length === 0) {
      setFilteredData([]);
      return;
    }
    data = data.filter(record =>
      selectedReliabilityLevels.includes(getReliabilityLevelForScore(record.reliability_score))
    );
    if (filterMode === 'sector') {
      data = data.filter((record) => {
        const aum = parseOptionalFiniteNumber(record.aum);
        const employeeAmount = parseOptionalFiniteNumber(record.employee_amount);
        const aumOk = aumMatchesSelectedBuckets(aum, selectedAumBuckets);
        const employeeOk = employeeMatchesSelectedBuckets(employeeAmount, selectedEmployeeBuckets);
        return aumOk && employeeOk;
      });
    }
    setFilteredData(data);
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applyFilters reads state; we depend on state vars explicitly
  }, [filterMode, selectedSectors, selectedCompanies, selectedReliabilityLevels, selectedAumBuckets, selectedEmployeeBuckets, marketData]);

  // Эффект печати в поисковой строке
  useEffect(() => {
    let index = 0;
    let timeoutId: ReturnType<typeof setTimeout>;
    const typeDelay = 80;
    const pauseAtEnd = 2500;

    const typeNext = () => {
      if (index <= SEARCH_PLACEHOLDER_FULL.length) {
        setTypewriterText(SEARCH_PLACEHOLDER_FULL.slice(0, index));
        if (index < SEARCH_PLACEHOLDER_FULL.length) {
          index += 1;
          timeoutId = setTimeout(typeNext, typeDelay);
        } else {
          timeoutId = setTimeout(restart, pauseAtEnd);
        }
      }
    };

    const restart = () => {
      index = 0;
      timeoutId = setTimeout(typeNext, typeDelay);
    };

    timeoutId = setTimeout(typeNext, typeDelay);
    return () => clearTimeout(timeoutId);
  }, []);

  // Обработчики для фильтра секторов
  const handleSectorToggle = (sector: string) => {
    setSelectedSectors(prev => 
      prev.includes(sector) 
        ? prev.filter(s => s !== sector)
        : [...prev, sector]
    );
  };

  const handleSelectAllSectors = () => {
    const uniqueSectors = Array.from(new Set(marketData.map(record => record.sector)));
    setSelectedSectors(uniqueSectors);
  };

  const handleClearAllSectors = () => {
    setSelectedSectors([]);
  };

  // Компании по секторам (уникальные по company_id)
  const companiesBySector = React.useMemo((): Map<string, CompanyItem[]> => {
    const map = new Map<string, CompanyItem[]>();
    const seen = new Set<string>();
    marketData.forEach((record) => {
      if (!record.sector) return;
      const id = record.company_id;
      if (seen.has(id)) return;
      seen.add(id);
      const name = record.company_name || record.company_id;
      const list = map.get(record.sector) || [];
      list.push({ id, name });
      map.set(record.sector, list);
    });
    const entries = Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    const sorted = new Map<string, CompanyItem[]>(entries);
    sorted.forEach((list: CompanyItem[]) => list.sort((a: CompanyItem, b: CompanyItem) => a.name.localeCompare(b.name)));
    return sorted;
  }, [marketData]);

  const handleCompanyToggle = (companyId: string) => {
    setSelectedCompanies((prev) =>
      prev.includes(companyId)
        ? prev.filter((c) => c !== companyId)
        : [...prev, companyId]
    );
  };

  const handleSelectAllCompanies = () => {
    const ids: string[] = [];
    companiesBySector.forEach((list: CompanyItem[]) => list.forEach((c: CompanyItem) => ids.push(c.id)));
    setSelectedCompanies(ids);
  };

  const handleClearAllCompanies = () => {
    setSelectedCompanies([]);
  };

  const handleToggleCompanySector = (sector: string) => {
    setExpandedCompanySectors((prev) =>
      prev.includes(sector) ? prev.filter((s) => s !== sector) : [...prev, sector]
    );
  };

  const handleSelectAllCompaniesInSector = (sector: string) => {
    const companies = companiesBySector.get(sector) || [];
    const ids = companies.map((c) => c.id);
    setSelectedCompanies((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const handleReliabilityToggle = (levelId: ReliabilityLevelId) => {
    setSelectedReliabilityLevels((prev) =>
      prev.includes(levelId)
        ? prev.filter((l) => l !== levelId)
        : [...prev, levelId]
    );
  };

  const handleSelectAllReliability = () => {
    setSelectedReliabilityLevels(RELIABILITY_LEVELS.map((l) => l.id));
  };

  const handleClearAllReliability = () => {
    setSelectedReliabilityLevels([]);
  };

  // Расчёт доступности данных при текущих фильтрах
  const filterStats = React.useMemo(() => {
    if (filteredData.length === 0) return null;
    const gradedData = filterRecordsWithMarketGroup(filteredData);
    const uniqueCompanies = new Set(filteredData.map(r => r.company_id)).size;
    const marketGroups = getSortedMarketGroups(gradedData);
    const total = marketGroups.length;
    if (total === 0) return null;

    let avgCount = 0, p50Count = 0, p2575Count = 0, p1090Count = 0;
    marketGroups.forEach((group) => {
      const posData = filterRecordsByMarketGroup(gradedData, group.key);
      const posCompanies = new Set(posData.map(r => r.company_id)).size;
      const n = posData.length;
      if (posCompanies >= 3 && n >= 5)  avgCount++;
      if (posCompanies >= 5 && n >= 10) p50Count++;
      if (posCompanies >= 7 && n >= 30) p2575Count++;
      if (posCompanies >= 10 && n >= 30) p1090Count++;
    });

    return {
      uniqueCompanies,
      total,
      records: filteredData.length,
      gradedRecords: gradedData.length,
      ungradedRecords: countRecordsWithoutMarketGroup(filteredData),
      avgCount,
      p50Count,
      p2575Count,
      p1090Count,
    };
  }, [filteredData]);

  const datasetMissingEmployeeScale = React.useMemo(() => {
    if (marketData.length === 0) return false;
    return !marketData.some((r) => parseOptionalFiniteNumber(r.employee_amount) !== null);
  }, [marketData]);

  useEffect(() => {
    if (!selectedIndustryId) {
      setGradingTree(null);
      return;
    }

    let cancelled = false;
    fetchCommunityGradingTreeCached()
      .then((data) => {
        if (!cancelled) setGradingTree(normalizeGradingTreePayload(data));
      })
      .catch(() => {
        if (!cancelled) setGradingTree(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedIndustryId]);

  const clusterMetas = React.useMemo(
    () => (gradingTree ? buildRoleClusterMetaList(gradingTree) : []),
    [gradingTree],
  );

  const clusterGroupingReady = clusterMetas.length > 0;

  const resolveDashboardGrouping = React.useCallback(
    (dashboardId: MarketDashboardId): MarketGroupingMode => {
      const mode = dashboardGrouping[dashboardId];
      if (mode === 'cluster' && !clusterGroupingReady) return 'grade';
      return mode;
    },
    [dashboardGrouping, clusterGroupingReady],
  );

  const setDashboardGroupingMode = React.useCallback(
    (dashboardId: MarketDashboardId, mode: MarketGroupingMode) => {
      setDashboardGrouping((prev) => ({ ...prev, [dashboardId]: mode }));
    },
    [],
  );

  const getDashboardMarketGroups = React.useCallback(
    (dashboardId: MarketDashboardId) =>
      getSortedMarketGroups(filteredData, resolveDashboardGrouping(dashboardId), clusterMetas),
    [filteredData, resolveDashboardGrouping, clusterMetas],
  );

  const getDashboardGroupRecords = React.useCallback(
    (dashboardId: MarketDashboardId, groupKey: string) =>
      filterRecordsByMarketGroup(
        filteredData,
        groupKey,
        resolveDashboardGrouping(dashboardId),
        clusterMetas,
      ),
    [filteredData, resolveDashboardGrouping, clusterMetas],
  );

  return (
    <div className="market-section">

      <div style={{ padding: '4px 0 20px' }}>
        <h1 style={{
          fontSize: '26px', fontWeight: '700', color: '#1a237e',
          margin: '0 0 18px 0', fontFamily: 'Poppins, sans-serif',
        }}>
          Обзор рынка
        </h1>
        <IndustrySelector
          value={selectedIndustryId}
          onChange={handleIndustryChange}
          options={industryOptions}
          optionsLoading={catalogLoading}
          compact
        />
      </div>

      {!selectedIndustryId && accessiblePackages.length > 0 && (
        <div style={{
          backgroundColor: '#e8eaf6',
          borderRadius: '10px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: '#1a237e',
          fontSize: '14px',
        }}>
          Доступно индустрий по подписке: {accessiblePackages.length}. Выберите индустрию или дождитесь автозагрузки дэшборда.
        </div>
      )}

      {!selectedIndustryId && !catalogLoading && accessiblePackages.length === 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '28px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          border: '1px dashed #c5cae9',
          color: '#555',
          fontSize: '14px',
        }}>
          Выберите индустрию, чтобы загрузить дэшборды и фильтры.
        </div>
      )}

      {selectedIndustryId && (catalogLoading || (loading && !isSelectedPackageAccessDenied)) && !selectedPackageDisplayName && (
        <div style={{ color: '#666', fontSize: '15px', padding: '8px 0 16px' }}>
          {catalogLoading ? 'Загружаем каталог индустрий...' : 'Загружаем дэшборды...'}
        </div>
      )}

      {isSelectedPackageAccessDenied && selectedCatalogPackage && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '32px 28px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #e1e5e9',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <i className="fas fa-lock" style={{ fontSize: '24px', color: '#e65100' }} aria-hidden="true" />
            </div>
            <div style={{ flex: 1, minWidth: '240px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1a237e',
                margin: '0 0 10px 0',
                fontFamily: 'Poppins, sans-serif',
              }}>
                Пакет «{selectedCatalogPackage.name}» недоступен
              </h3>
              <p style={{ fontSize: '14px', color: '#555', margin: '0 0 16px 0', lineHeight: 1.65 }}>
                У вашего аккаунта нет подписки на рыночные данные по этой индустрии.
                Вы можете запросить презентацию пакета или обратиться к консультанту datacircle для покупки доступа.
              </p>
              {selectedCatalogPackage.description && (
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 14px 0', lineHeight: 1.5 }}>
                  {normalizePackageDescription(selectedCatalogPackage.description)}
                </p>
              )}
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <CatalogStat
                  icon="fa-building"
                  label={`${selectedCatalogPackage.companies_data.length} компаний в пакете`}
                />
                <CatalogStat
                  icon="fa-industry"
                  label={`${getUniqueSectors(selectedCatalogPackage.companies_data).length} секторов`}
                />
                <CatalogStat
                  icon="fa-user-tie"
                  label={`${selectedCatalogPackage.positions?.length || 0} ролей`}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    setDetailPackage(selectedCatalogPackage);
                    setIsDetailModalOpen(true);
                  }}
                  style={{
                    padding: '10px 22px',
                    background: 'white',
                    color: '#1a237e',
                    border: '1px solid #c5cae9',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Подробнее о пакете
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAccessDeniedModal({
                      isOpen: true,
                      packageName: selectedCatalogPackage.name,
                      dataPackageId: selectedCatalogPackage.id,
                    });
                  }}
                  style={{
                    padding: '10px 22px',
                    background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Запросить презентацию
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedIndustryId && !catalogLoading && !selectedCatalogPackage && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          color: '#666',
          fontSize: '14px',
          border: '1px dashed #c5cae9',
        }}>
          Пакет для выбранной индустрии не найден в каталоге.
        </div>
      )}

      {/* Отображение загруженных данных */}
      {selectedIndustryId && selectedPackageDisplayName && !isSelectedPackageAccessDenied && (
        <div style={{ marginTop: '24px' }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#1a237e',
            margin: '0 0 16px 0'
          }}>
            Данные пакета: {selectedPackageDisplayName}
          </h3>

          {/* Блок фильтров */}
          {marketData.length > 0 && (
            <div
              data-tour="market-filters"
              style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}>
              <button
                type="button"
                onClick={() => setIsFiltersExpanded((prev) => !prev)}
                aria-expanded={isFiltersExpanded}
                style={{
                  width: '100%',
                  border: '1px solid rgba(26, 35, 126, 0.18)',
                  borderRadius: '8px',
                  background: isFiltersExpanded ? 'rgba(26, 35, 126, 0.06)' : '#f8f9fa',
                  color: '#1a237e',
                  padding: '10px 12px',
                  marginBottom: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-sliders-h" aria-hidden="true" />
                  Фильтры
                </span>
                <i className={`fas ${isFiltersExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
              </button>

              {isFiltersExpanded && (
              <>
              <div className="market-filter-mode-switch">
                <button
                  type="button"
                  className={`market-filter-mode-btn ${filterMode === 'sector' ? 'active' : ''}`}
                  onClick={() => setFilterMode('sector')}
                >
                  <i className="fas fa-chart-pie" aria-hidden="true"></i>
                  <span>По секторам</span>
                </button>
                <button
                  type="button"
                  className={`market-filter-mode-btn ${filterMode === 'company' ? 'active' : ''}`}
                  onClick={() => setFilterMode('company')}
                >
                  <i className="fas fa-building" aria-hidden="true"></i>
                  <span>По компаниям</span>
                </button>
              </div>
              
              {filterMode === 'sector' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  Сектор (выбрано {selectedSectors.length} из {Array.from(new Set(marketData.map(record => record.sector))).length}):
                </label>
                
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  {Array.from(new Set(marketData.map(record => record.sector))).map(sector => (
                    <label key={sector} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#333'
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedSectors.includes(sector)}
                        onChange={() => handleSectorToggle(sector)}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#1a237e'
                        }}
                      />
                      {sector}
                    </label>
                  ))}
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <button
                    onClick={handleSelectAllSectors}
                    style={{
                      backgroundColor: '#1a237e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#283593';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#1a237e';
                    }}
                  >
                    Выбрать все
                  </button>
                  
                  <button
                    onClick={handleClearAllSectors}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e0e0e0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                  >
                    Очистить
                  </button>
                </div>
              </div>
              )}

              {filterMode === 'company' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  Компании (выбрано {selectedCompanies.length} из {companiesBySector.size > 0 ? Array.from(companiesBySector.values()).reduce((sum, list) => sum + list.length, 0) : 0}):
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <button
                    type="button"
                    onClick={handleSelectAllCompanies}
                    style={{
                      backgroundColor: '#1a237e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#283593'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1a237e'; }}
                  >
                    Выбрать все
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAllCompanies}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e0e0e0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                  >
                    Очистить
                  </button>
                </div>
                <div className="market-companies-by-sector">
                  {Array.from(companiesBySector.entries()).map(([sector, companies]) => (
                    <div key={sector} style={{ marginBottom: '16px' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        marginBottom: '8px',
                        paddingBottom: '4px',
                        borderBottom: '1px solid rgba(26, 35, 126, 0.2)'
                      }}>
                        <button
                          type="button"
                          onClick={() => handleToggleCompanySector(sector)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#1a237e'
                          }}
                        >
                          <i className={`fas ${expandedCompanySectors.includes(sector) ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true" />
                          <span>{sector}</span>
                          <span style={{ color: '#6b7280', fontWeight: 500 }}>({companies.length})</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSelectAllCompaniesInSector(sector)}
                          style={{
                            backgroundColor: '#eef2ff',
                            color: '#1a237e',
                            border: '1px solid #c5cae9',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Выбрать все
                        </button>
                      </div>
                      {expandedCompanySectors.includes(sector) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                        {companies.map(({ id, name }) => (
                          <label
                            key={id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              color: '#333'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCompanies.includes(id)}
                              onChange={() => handleCompanyToggle(id)}
                              style={{ width: '14px', height: '14px', accentColor: '#1a237e', flexShrink: 0 }}
                            />
                            <span>{name}</span>
                          </label>
                        ))}
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px',
                  display: 'block'
                }}>
                  <i className="fas fa-shield-alt" style={{ marginRight: '6px', color: '#1a237e', fontSize: '13px' }} aria-hidden="true"></i>
                  Дополнительно: надежность (выбрано {selectedReliabilityLevels.length} из {RELIABILITY_LEVELS.length})
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                  {RELIABILITY_LEVELS.map((level) => (
                    <label
                      key={level.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#333'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedReliabilityLevels.includes(level.id)}
                        onChange={() => handleReliabilityToggle(level.id)}
                        style={{ width: '16px', height: '16px', accentColor: '#1a237e', flexShrink: 0 }}
                      />
                      <span
                        style={{
                          display: 'inline-block',
                          width: '16px',
                          height: '16px',
                          borderRadius: '2px',
                          backgroundColor: level.color,
                          flexShrink: 0
                        }}
                        aria-hidden
                      />
                      <span>{level.label}</span>
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={handleSelectAllReliability}
                    style={{
                      backgroundColor: '#1a237e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#283593'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1a237e'; }}
                  >
                    Выбрать все
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAllReliability}
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e0e0e0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                  >
                    Очистить
                  </button>
                </div>
              </div>

              {filterMode === 'sector' && (
                <>
              {datasetMissingEmployeeScale && (
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #ffe082',
                    background: '#fff8e1',
                    color: '#6d4c41',
                    fontSize: '12px',
                    lineHeight: 1.45,
                  }}
                  role="status"
                >
                  <div>
                    В ответе API нет численности сотрудников (все значения пустые). В базе она уже может быть
                    заполнена — тогда на сервере API нужно задеплоить текущий backend (эндпоинт{' '}
                    <code style={{ fontSize: '11px' }}>/market/records</code> с полем{' '}
                    <code style={{ fontSize: '11px' }}>employee_amount</code>).
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <EmployeeBucketFilter
                  selected={selectedEmployeeBuckets}
                  onChange={setSelectedEmployeeBuckets}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <AumBucketFilter
                  selected={selectedAumBuckets}
                  onChange={setSelectedAumBuckets}
                />
              </div>
              </>
              )}
              
              <div style={{
                fontSize: '12px',
                color: '#666',
                padding: '8px 12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #e9ecef'
              }}>
                {filterMode === 'sector' && (
                  <>Выбрано: {selectedSectors.length} из {Array.from(new Set(marketData.map(record => record.sector))).length} секторов</>
                )}
                {filterMode === 'company' && (
                  <>Выбрано: {selectedCompanies.length} из {companiesBySector.size > 0 ? Array.from(companiesBySector.values()).reduce((sum, list) => sum + list.length, 0) : 0} компаний</>
                )}
                {selectedReliabilityLevels.length > 0 && selectedReliabilityLevels.length < RELIABILITY_LEVELS.length && (
                  <span style={{ marginLeft: '12px' }}>
                    • Надежность: {selectedReliabilityLevels.length} из {RELIABILITY_LEVELS.length}
                  </span>
                )}
                {filteredData.length > 0 && (
                  <span style={{ marginLeft: '12px' }}>
                    • Показано {filteredData.length} записей
                  </span>
                )}
              </div>

              {/* Панель доступности данных */}
              {filterStats && (
                <div
                  data-tour="market-availability"
                  style={{
                  marginTop: '16px',
                  padding: '14px 16px',
                  borderRadius: '8px',
                  backgroundColor: '#f0f4ff',
                  border: '1px solid #c5cae9',
                  overflow: 'visible',
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#1a237e', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fas fa-info-circle" aria-hidden="true" />
                    Доступность данных при текущих фильтрах
                  </div>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#444' }}>
                      <span style={{ fontWeight: '600', color: '#1a237e' }}>{filterStats.uniqueCompanies}</span> компаний
                    </span>
                    <span style={{ fontSize: '12px', color: '#444' }}>
                      <span style={{ fontWeight: '600', color: '#1a237e' }}>{filterStats.records}</span> записей
                    </span>
                    <span style={{ fontSize: '12px', color: '#444' }}>
                      <span style={{ fontWeight: '600', color: '#1a237e' }}>{filterStats.total}</span> ролей
                    </span>
                    {filterStats.ungradedRecords > 0 && (
                      <span style={{ fontSize: '12px', color: '#c62828' }}>
                        {filterStats.ungradedRecords} записей без привязки к роли не участвуют в таблицах
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', overflow: 'visible' }}>
                    {[
                      { label: 'Среднее', count: filterStats.avgCount, req: 'от 3 компаний, 5+ записей' },
                      { label: 'Медиана', count: filterStats.p50Count, req: 'от 5 компаний, 10+ записей' },
                      { label: '25-й перцентиль / 75-й перцентиль', count: filterStats.p2575Count, req: 'от 7 компаний, 30+ записей' },
                      { label: '10-й перцентиль / 90-й перцентиль', count: filterStats.p1090Count, req: 'от 10 компаний, 30+ записей' },
                    ].map(({ label, count, req }, metricIndex) => {
                      const ratio = count / filterStats.total;
                      const bg = ratio === 0 ? '#ffebee' : ratio < 1 ? '#fff8e1' : '#e8f5e9';
                      const border = ratio === 0 ? '#ef9a9a' : ratio < 1 ? '#ffe082' : '#a5d6a7';
                      const textColor = ratio === 0 ? '#c62828' : ratio < 1 ? '#e65100' : '#1b5e20';
                      const icon = ratio === 0 ? 'fa-times-circle' : ratio < 1 ? 'fa-exclamation-circle' : 'fa-check-circle';
                      const isTooltipOpen = hoveredFilterMetric === label;
                      const tooltipId = `filter-metric-tip-${metricIndex}`;
                      return (
                        <div
                          key={label}
                          tabIndex={0}
                          role="button"
                          aria-label={`${label}: ${count} из ${filterStats.total}. Требования: ${req}`}
                          aria-describedby={isTooltipOpen ? tooltipId : undefined}
                          onMouseEnter={() => setHoveredFilterMetric(label)}
                          onMouseLeave={() => setHoveredFilterMetric(null)}
                          onFocus={() => setHoveredFilterMetric(label)}
                          onBlur={() => setHoveredFilterMetric(null)}
                          style={{
                            position: 'relative',
                            display: 'flex', alignItems: 'center', gap: '5px',
                            padding: '4px 10px', borderRadius: '20px',
                            backgroundColor: bg, border: `1px solid ${border}`,
                            fontSize: '12px', fontWeight: '500', color: textColor,
                            cursor: 'help',
                            outline: 'none',
                          }}
                        >
                          <i className={`fas ${icon}`} aria-hidden="true" style={{ fontSize: '11px' }} />
                          <span>{label}</span>
                          <span style={{ fontWeight: '400', opacity: 0.8 }}>
                            {count}/{filterStats.total}
                          </span>
                          <span
                            id={tooltipId}
                            role="tooltip"
                            style={{
                              position: 'absolute',
                              bottom: 'calc(100% + 8px)',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              backgroundColor: '#1a237e',
                              color: '#fff',
                              fontSize: '11px',
                              lineHeight: 1.35,
                              padding: '6px 10px',
                              borderRadius: '6px',
                              whiteSpace: 'nowrap',
                              opacity: isTooltipOpen ? 1 : 0,
                              visibility: isTooltipOpen ? 'visible' : 'hidden',
                              pointerEvents: 'none',
                              transition: 'opacity 0.15s ease',
                              zIndex: 30,
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
                            }}
                          >
                            {req}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>
                    Числа показывают количество ролей, по которым метрика будет отображена. Наведите на метку, чтобы увидеть требования.
                  </div>
                </div>
              )}
              </>
              )}
            </div>
          )}

          <div className="market-excel-export">
            <div className="market-excel-export-inner">
              <div className="market-excel-export-left">
                <div className="market-excel-export-title">
                  <i className="fas fa-file-alt" aria-hidden="true" style={{ marginRight: '8px' }}></i>
                  Отчеты
                </div>
                <div className="market-excel-export-subtitle">
                  Сформируйте отчет на основе выбранных фильтров и текущих данных на странице.
                </div>
                {exportXlsxError && (
                  <div className="market-excel-export-error" role="alert">
                    {exportXlsxError}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={`market-excel-export-btn ${isExportingXlsx ? 'is-exporting' : ''}`}
                onClick={handleExportXlsx}
                disabled={isExportingXlsx}
                title="Сгенерировать отчет (xlsx)"
              >
                <i className="fas fa-file-excel" aria-hidden="true"></i>
                <span>{isExportingXlsx ? 'Формируем…' : 'Сгенерировать отчет (xlsx)'}</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#666'
            }}>
              Загрузка данных...
            </div>
          ) : filteredData.length > 0 ? (
            <div>
              <MarketDashboardSectionHeader
                title="Фиксированное вознаграждение (месяц, GROSS)"
                groupingMode={resolveDashboardGrouping('fix')}
                onGroupingModeChange={(mode) => setDashboardGroupingMode('fix', mode)}
              />
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                overflowX: 'auto',
                overflowY: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <table style={MARKET_MONEY_TABLE_STYLE}>
                  <thead>
                    <tr style={{
                      backgroundColor: '#1a237e',
                      color: 'white'
                    }}>
                      <th style={{ 
                        padding: '16px 12px', 
                        textAlign: 'left', 
                        fontWeight: '600',
                        fontSize: '16px',
                        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                        width: '200px'
                      }}>
                        {getMarketGroupColumnLabel(resolveDashboardGrouping('fix'))}
                      </th>
                      <th style={{ 
                        padding: '16px 12px', 
                        textAlign: 'center', 
                        fontWeight: '600',
                        fontSize: '16px',
                        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                        width: '140px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: '8px'
                        }}>
                          Надежность данных
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsReliabilityModalOpen(true);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '14px',
                              color: 'white',
                              padding: '0',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '50%',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <i className="fas fa-question-circle"></i>
                          </button>
                        </div>
                      </th>
                      <th style={{ 
                        padding: '16px 12px', 
                        textAlign: 'center', 
                        fontWeight: '600',
                        fontSize: '16px',
                        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                        width: '100px'
                      }}>
                        Кол-во датакейсов
                      </th>
                      <th style={MARKET_PERCENTILE_HEADER_STYLE}>
                        10p
                      </th>
                      <th style={MARKET_PERCENTILE_HEADER_STYLE}>
                        25p
                      </th>
                      <th style={MARKET_PERCENTILE_HEADER_STYLE}>
                        50p
                      </th>
                      <th style={MARKET_PERCENTILE_HEADER_STYLE}>
                        75p
                      </th>
                      <th style={MARKET_PERCENTILE_HEADER_STYLE}>
                        90p
                      </th>
                      <th style={MARKET_PERCENTILE_HEADER_LAST_STYLE}>
                        Среднее
                      </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Функция для вычисления перцентиля
                    const calculatePercentile = (data: number[], percentile: number): number => {
                      if (data.length === 0) return 0;
                      const sorted = [...data].sort((a, b) => a - b);
                      const index = Math.ceil((percentile / 100) * sorted.length) - 1;
                      return sorted[Math.max(0, index)];
                    };

                    const getAllowedPercentiles = (n: number, ps: readonly number[]) => {
                      const seenIndexes = new Set<number>();
                      const allowed = new Set<number>();
                      for (const p of ps) {
                        const idx = Math.ceil((p / 100) * n) - 1;
                        if (!seenIndexes.has(idx)) {
                          seenIndexes.add(idx);
                          allowed.add(p);
                        }
                      }
                      return allowed;
                    };

                    const percentiles = [10, 25, 50, 75, 90] as const;

                    // Получаем уникальные позиции из отфильтрованных данных
                    const marketGroups = getDashboardMarketGroups('fix');
                    
                    return marketGroups.map((group, index) => {
                      const positionData = getDashboardGroupRecords('fix', group.key);
                      
                      // Извлекаем значения fix и reliability_score
                      const fixValues = positionData.map(record => record.fix);
                      const allowed = getAllowedPercentiles(fixValues.length, percentiles);
                      const reliabilityValues = positionData.map(record => record.reliability_score);
                      
                      // Вычисляем перцентили
                      const uniqueCompanyCount = new Set(positionData.map(r => r.company_id)).size;
                      const p10eligible = allowed.has(10) && uniqueCompanyCount >= 10 && positionData.length >= 30;
                      const p25eligible = allowed.has(25) && uniqueCompanyCount >= 7 && positionData.length >= 30;
                      const p50eligible = allowed.has(50) && uniqueCompanyCount >= 5 && positionData.length >= 10;
                      const avgEligible = uniqueCompanyCount >= 3 && positionData.length >= 5;
                      const p75eligible = allowed.has(75) && uniqueCompanyCount >= 7 && positionData.length >= 30;
                      const p90eligible = allowed.has(90) && uniqueCompanyCount >= 10 && positionData.length >= 30;
                      const p10 = p10eligible ? calculatePercentile(fixValues, 10) : null;
                      const p25 = p25eligible ? calculatePercentile(fixValues, 25) : null;
                      const p50 = p50eligible ? calculatePercentile(fixValues, 50) : null;
                      const avgFix = avgEligible ? Math.round(fixValues.reduce((s, v) => s + v, 0) / fixValues.length) : null;
                      const p75 = p75eligible ? calculatePercentile(fixValues, 75) : null;
                      const p90 = p90eligible ? calculatePercentile(fixValues, 90) : null;
                      
                      // Вычисляем среднюю надежность
                      const avgReliability = reliabilityValues.length > 0 
                        ? reliabilityValues.reduce((sum, val) => sum + val, 0) / reliabilityValues.length
                        : 0;

                      // Функция для определения уровня надежности
                      const getReliabilityLevel = (score: number) => {
                        if (score > 0.8) {
                          return { 
                            level: 'Excellent', 
                            color: '#073763', 
                            text: 'Очень высокая достоверность (Excellent)',
                            shortText: 'Excellent'
                          };
                        } else if (score > 0.5) {
                          return { 
                            level: 'High', 
                            color: '#1155cc', 
                            text: 'Высокая достоверность (High)',
                            shortText: 'High'
                          };
                        } else if (score > 0.3) {
                          return { 
                            level: 'Moderate', 
                            color: '#6d9eeb', 
                            text: 'Умеренная достоверность (Moderate)',
                            shortText: 'Moderate'
                          };
                        } else {
                          return { 
                            level: 'Low', 
                            color: '#999999', 
                            text: 'Информация требует проверки (Low)',
                            shortText: 'Low'
                          };
                        }
                      };

                      const reliabilityLevel = getReliabilityLevel(avgReliability);
                      
                      return (
                        <tr 
                          key={group.key} 
                          style={{
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e3f2fd';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#fafafa';
                          }}
                        >
                          <td style={{ 
                            padding: '12px 12px',
                            fontWeight: '500',
                            color: '#333'
                          }}>
                            <MarketGroupLabelCell group={group} />
                          </td>
                          <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px'
                            }}>
                              <div style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: reliabilityLevel.color,
                                borderRadius: '3px',
                                border: '1px solid #e0e0e0'
                              }}></div>
                              <span style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                color: '#333'
                              }}>
                                {reliabilityLevel.shortText}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                            {positionData.length}
                          </td>
                          <td style={MARKET_PERCENTILE_CELL_STYLE}>
                            {p10 === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(p10)} ₽`}
                          </td>
                          <td style={MARKET_PERCENTILE_CELL_STYLE}>
                            {p25 === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(p25)} ₽`}
                          </td>
                          <td style={MARKET_PERCENTILE_CELL_STYLE}>
                            {p50 === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(p50)} ₽`}
                          </td>
                          <td style={MARKET_PERCENTILE_CELL_STYLE}>
                            {p75 === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(p75)} ₽`}
                          </td>
                          <td style={MARKET_PERCENTILE_CELL_STYLE}>
                            {p90 === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(p90)} ₽`}
                          </td>
                          <td style={MARKET_PERCENTILE_CELL_STYLE}>
                            {avgFix === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(avgFix)} ₽`}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
              </div>
            </div>
          ) : (
            <div style={{
              padding: '40px 24px',
              textAlign: 'center',
              color: '#666'
            }}>
              Данные не найдены
            </div>
          )}
        </div>
      )}

      {/* Таблица Target STI */}
      {filteredData.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <MarketDashboardSectionHeader
            title={(
              <>
                Нацеленное краткосрочное переменное вознаграждение (Target STI)
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    aria-label="% от годового фиксированного дохода"
                    onMouseEnter={() => setShowStiTooltip(true)}
                    onMouseLeave={() => setShowStiTooltip(false)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: '1px solid #9aa7db',
                      color: '#1a237e',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'help',
                      backgroundColor: '#eef2ff',
                    }}
                  >
                    ?
                  </span>
                  <span
                    style={{
                      position: 'absolute',
                      left: '24px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: '#1a237e',
                      color: '#fff',
                      fontSize: '12px',
                      lineHeight: 1.2,
                      padding: '6px 8px',
                      borderRadius: '6px',
                      whiteSpace: 'nowrap',
                      opacity: showStiTooltip ? 1 : 0,
                      pointerEvents: 'none',
                      transition: 'opacity 0.15s ease',
                      zIndex: 10,
                    }}
                  >
                    % от годового фиксированного дохода
                  </span>
                </span>
              </>
            )}
            groupingMode={resolveDashboardGrouping('sti')}
            onGroupingModeChange={(mode) => setDashboardGroupingMode('sti', mode)}
          />
          
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            overflowX: 'auto',
            overflowY: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              tableLayout: 'fixed'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: '#1a237e',
                  color: 'white'
                }}>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '200px'
                  }}>
                    {getMarketGroupColumnLabel(resolveDashboardGrouping('sti'))}
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '140px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: '8px'
                    }}>
                      Надежность данных
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsReliabilityModalOpen(true);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: 'white',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="fas fa-question-circle"></i>
                      </button>
                    </div>
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '100px'
                  }}>
                    Кол-во датакейсов
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '80px'
                  }}>
                    10p
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '80px'
                  }}>
                    25p
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '80px'
                  }}>
                    50p
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '80px'
                  }}>
                    75p
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '80px'
                  }}>
                    90p
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    width: '80px'
                  }}>
                    Среднее
                  </th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Функция для вычисления перцентиля
                  const calculatePercentile = (data: number[], percentile: number): number => {
                    if (data.length === 0) return 0;
                    const sorted = [...data].sort((a, b) => a - b);
                    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
                    return sorted[Math.max(0, index)];
                  };

                  const getAllowedPercentiles = (n: number, ps: readonly number[]) => {
                    const seenIndexes = new Set<number>();
                    const allowed = new Set<number>();
                    for (const p of ps) {
                      const idx = Math.ceil((p / 100) * n) - 1;
                      if (!seenIndexes.has(idx)) {
                        seenIndexes.add(idx);
                        allowed.add(p);
                      }
                    }
                    return allowed;
                  };

                  const percentiles = [10, 25, 50, 75, 90] as const;

                  // Получаем уникальные позиции из отфильтрованных данных
                  const marketGroups = getDashboardMarketGroups('sti');
                  
                  return marketGroups.map((group, index) => {
                    const positionData = getDashboardGroupRecords('sti', group.key);
                    
                    // Извлекаем значения sti и reliability_score
                    const stiValues = positionData.map(record => record.sti);
                    const allowed = getAllowedPercentiles(stiValues.length, percentiles);
                    const reliabilityValues = positionData.map(record => record.reliability_score);
                    
                    // Вычисляем перцентили для STI
                    const uniqueCompanyCountStiUi = new Set(positionData.map(r => r.company_id)).size;
                    const p10eligibleStiUi = allowed.has(10) && uniqueCompanyCountStiUi >= 10 && positionData.length >= 30;
                    const p25eligibleStiUi = allowed.has(25) && uniqueCompanyCountStiUi >= 7 && positionData.length >= 30;
                    const p50eligibleStiUi = allowed.has(50) && uniqueCompanyCountStiUi >= 5 && positionData.length >= 10;
                    const avgEligibleStiUi = uniqueCompanyCountStiUi >= 3 && positionData.length >= 5;
                    const p75eligibleStiUi = allowed.has(75) && uniqueCompanyCountStiUi >= 7 && positionData.length >= 30;
                    const p90eligibleStiUi = allowed.has(90) && uniqueCompanyCountStiUi >= 10 && positionData.length >= 30;
                    const p10 = p10eligibleStiUi ? calculatePercentile(stiValues, 10) : null;
                    const p25 = p25eligibleStiUi ? calculatePercentile(stiValues, 25) : null;
                    const p50 = p50eligibleStiUi ? calculatePercentile(stiValues, 50) : null;
                    const avgStiUi = avgEligibleStiUi ? stiValues.reduce((s, v) => s + v, 0) / stiValues.length : null;
                    const p75 = p75eligibleStiUi ? calculatePercentile(stiValues, 75) : null;
                    const p90 = p90eligibleStiUi ? calculatePercentile(stiValues, 90) : null;
                    
                    // Вычисляем среднюю надежность
                    const avgReliability = reliabilityValues.length > 0 
                      ? reliabilityValues.reduce((sum, val) => sum + val, 0) / reliabilityValues.length
                      : 0;

                    // Функция для определения уровня надежности
                    const getReliabilityLevel = (score: number) => {
                      if (score > 0.8) {
                        return { 
                          level: 'Excellent', 
                          color: '#073763', 
                          text: 'Очень высокая достоверность (Excellent)',
                          shortText: 'Excellent'
                        };
                      } else if (score > 0.5) {
                        return { 
                          level: 'High', 
                          color: '#1155cc', 
                          text: 'Высокая достоверность (High)',
                          shortText: 'High'
                        };
                      } else if (score > 0.3) {
                        return { 
                          level: 'Moderate', 
                          color: '#6d9eeb', 
                          text: 'Умеренная достоверность (Moderate)',
                          shortText: 'Moderate'
                        };
                      } else {
                        return { 
                          level: 'Low', 
                          color: '#999999', 
                          text: 'Информация требует проверки (Low)',
                          shortText: 'Low'
                        };
                      }
                    };

                    const reliabilityLevel = getReliabilityLevel(avgReliability);
                    
                    return (
                      <tr 
                        key={group.key} 
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e3f2fd';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#fafafa';
                        }}
                      >
                        <td style={{ 
                          padding: '12px 12px',
                          fontWeight: '500',
                          color: '#333'
                        }}>
                          <MarketGroupLabelCell group={group} />
                        </td>
                        <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}>
                            <div style={{
                              width: '16px',
                              height: '16px',
                              backgroundColor: reliabilityLevel.color,
                              borderRadius: '3px',
                              border: '1px solid #e0e0e0'
                            }}></div>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: '#333'
                            }}>
                              {reliabilityLevel.shortText}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                          {positionData.length}
                        </td>
                        <td style={{ 
                          padding: '12px 12px', 
                          textAlign: 'center',
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {p10 === null ? '—' : `${(p10 * 100).toFixed(1)}%`}
                        </td>
                        <td style={{ 
                          padding: '12px 12px', 
                          textAlign: 'center',
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {p25 === null ? '—' : `${(p25 * 100).toFixed(1)}%`}
                        </td>
                        <td style={{ 
                          padding: '12px 12px', 
                          textAlign: 'center',
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {p50 === null ? '—' : `${(p50 * 100).toFixed(1)}%`}
                        </td>
                        <td style={{ 
                          padding: '12px 12px', 
                          textAlign: 'center',
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {p75 === null ? '—' : `${(p75 * 100).toFixed(1)}%`}
                        </td>
                        <td style={{ 
                          padding: '12px 12px', 
                          textAlign: 'center',
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {p90 === null ? '—' : `${(p90 * 100).toFixed(1)}%`}
                        </td>
                        <td style={{ 
                          padding: '12px 12px', 
                          textAlign: 'center',
                          fontSize: '11px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {avgStiUi === null ? '—' : `${(avgStiUi * 100).toFixed(1)}%`}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Таблица Total cash at target */}
      {filteredData.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <MarketDashboardSectionHeader
            title={(
              <>
                Нацеленное совокупное вознаграждение (Total cash at target)
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    aria-label="без учета LTIP"
                    onMouseEnter={() => setShowTotalCompTooltip(true)}
                    onMouseLeave={() => setShowTotalCompTooltip(false)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: '1px solid #9aa7db',
                      color: '#1a237e',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'help',
                      backgroundColor: '#eef2ff'
                    }}
                  >
                    ?
                  </span>
                  <span
                    style={{
                      position: 'absolute',
                      left: '24px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: '#1a237e',
                      color: '#fff',
                      fontSize: '12px',
                      lineHeight: 1.2,
                      padding: '6px 8px',
                      borderRadius: '6px',
                      whiteSpace: 'nowrap',
                      opacity: showTotalCompTooltip ? 1 : 0,
                      pointerEvents: 'none',
                      transition: 'opacity 0.15s ease',
                      zIndex: 10,
                    }}
                  >
                    без учета LTIP
                  </span>
                </span>
              </>
            )}
            groupingMode={resolveDashboardGrouping('total')}
            onGroupingModeChange={(mode) => setDashboardGroupingMode('total', mode)}
          />

          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            overflowX: 'auto',
            overflowY: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}>
            <table style={MARKET_MONEY_TABLE_STYLE}>
              <thead>
                <tr style={{
                  backgroundColor: '#1a237e',
                  color: 'white'
                }}>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '200px'
                  }}>
                    {getMarketGroupColumnLabel(resolveDashboardGrouping('total'))}
                  </th>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '140px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}>
                      Надежность данных
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsReliabilityModalOpen(true);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: 'white',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="fas fa-question-circle"></i>
                      </button>
                    </div>
                  </th>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '100px'
                  }}>
                    Кол-во датакейсов
                  </th>
                  <th style={MARKET_PERCENTILE_HEADER_STYLE}>10p</th>
                  <th style={MARKET_PERCENTILE_HEADER_STYLE}>25p</th>
                  <th style={MARKET_PERCENTILE_HEADER_STYLE}>50p</th>
                  <th style={MARKET_PERCENTILE_HEADER_STYLE}>75p</th>
                  <th style={MARKET_PERCENTILE_HEADER_STYLE}>90p</th>
                  <th style={MARKET_PERCENTILE_HEADER_LAST_STYLE}>Среднее</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const calculatePercentile = (data: number[], percentile: number): number => {
                    if (data.length === 0) return 0;
                    const sorted = [...data].sort((a, b) => a - b);
                    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
                    return sorted[Math.max(0, index)];
                  };

                  const getAllowedPercentiles = (n: number, ps: readonly number[]) => {
                    const seenIndexes = new Set<number>();
                    const allowed = new Set<number>();
                    for (const p of ps) {
                      const idx = Math.ceil((p / 100) * n) - 1;
                      if (!seenIndexes.has(idx)) {
                        seenIndexes.add(idx);
                        allowed.add(p);
                      }
                    }
                    return allowed;
                  };

                  const percentiles = [10, 25, 50, 75, 90] as const;
                  const marketGroups = getDashboardMarketGroups('total');
                  
                  return marketGroups.map((group, index) => {
                    const positionData = getDashboardGroupRecords('total', group.key);
                    const totalValues = positionData.map(record => (record.fix * 12) * (1 + record.sti));
                    const allowed = getAllowedPercentiles(totalValues.length, percentiles);
                    const reliabilityValues = positionData.map(record => record.reliability_score);

                    const uniqueCompanyCountTotalUi = new Set(positionData.map(r => r.company_id)).size;
                    const p10eligibleTotalUi = allowed.has(10) && uniqueCompanyCountTotalUi >= 10 && positionData.length >= 30;
                    const p25eligibleTotalUi = allowed.has(25) && uniqueCompanyCountTotalUi >= 7 && positionData.length >= 30;
                    const p50eligibleTotalUi = allowed.has(50) && uniqueCompanyCountTotalUi >= 5 && positionData.length >= 10;
                    const avgEligibleTotalUi = uniqueCompanyCountTotalUi >= 3 && positionData.length >= 5;
                    const p75eligibleTotalUi = allowed.has(75) && uniqueCompanyCountTotalUi >= 7 && positionData.length >= 30;
                    const p90eligibleTotalUi = allowed.has(90) && uniqueCompanyCountTotalUi >= 10 && positionData.length >= 30;
                    const p10 = p10eligibleTotalUi ? calculatePercentile(totalValues, 10) : null;
                    const p25 = p25eligibleTotalUi ? calculatePercentile(totalValues, 25) : null;
                    const p50 = p50eligibleTotalUi ? calculatePercentile(totalValues, 50) : null;
                    const avgTotalUi = avgEligibleTotalUi ? Math.round(totalValues.reduce((s, v) => s + v, 0) / totalValues.length) : null;
                    const p75 = p75eligibleTotalUi ? calculatePercentile(totalValues, 75) : null;
                    const p90 = p90eligibleTotalUi ? calculatePercentile(totalValues, 90) : null;

                    const avgReliability = reliabilityValues.length > 0
                      ? reliabilityValues.reduce((sum, val) => sum + val, 0) / reliabilityValues.length
                      : 0;

                    const getReliabilityLevel = (score: number) => {
                      if (score > 0.8) return { color: '#073763', shortText: 'Excellent' };
                      if (score > 0.5) return { color: '#1155cc', shortText: 'High' };
                      if (score > 0.3) return { color: '#6d9eeb', shortText: 'Moderate' };
                      return { color: '#999999', shortText: 'Low' };
                    };

                    const reliabilityLevel = getReliabilityLevel(avgReliability);

                    return (
                      <tr
                        key={group.key}
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e3f2fd'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#fafafa'; }}
                      >
                        <td style={{ padding: '12px 12px', fontWeight: '500', color: '#333' }}><MarketGroupLabelCell group={group} /></td>
                        <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <div style={{ width: '16px', height: '16px', backgroundColor: reliabilityLevel.color, borderRadius: '3px', border: '1px solid #e0e0e0' }}></div>
                            <span style={{ fontSize: '12px', fontWeight: '500', color: '#333' }}>{reliabilityLevel.shortText}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 12px', textAlign: 'center' }}>{positionData.length}</td>
                        <td style={MARKET_PERCENTILE_CELL_STYLE}>{p10 === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(p10)} ₽`}</td>
                        <td style={MARKET_PERCENTILE_CELL_STYLE}>{p25 === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(p25)} ₽`}</td>
                        <td style={MARKET_PERCENTILE_CELL_STYLE}>{p50 === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(p50)} ₽`}</td>
                        <td style={MARKET_PERCENTILE_CELL_STYLE}>{p75 === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(p75)} ₽`}</td>
                        <td style={MARKET_PERCENTILE_CELL_STYLE}>{p90 === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(p90)} ₽`}</td>
                        <td style={MARKET_PERCENTILE_CELL_STYLE}>{avgTotalUi === null ? '—' : `${new Intl.NumberFormat('ru-RU').format(avgTotalUi)} ₽`}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Таблица LTI */}
      {filteredData.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <div style={{ width: 'fit-content', maxWidth: '100%', margin: '0 auto' }}>
          <MarketDashboardSectionHeader
            title="Долгосрочное вознаграждение (LTI)"
            groupingMode={resolveDashboardGrouping('ltip')}
            onGroupingModeChange={(mode) => setDashboardGroupingMode('ltip', mode)}
          />
          
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              overflowX: 'auto',
              overflowY: 'hidden',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
              <table style={{
                width: 'auto',
                borderCollapse: 'collapse',
                fontSize: '14px',
                tableLayout: 'fixed'
              }}>
              <thead>
                <tr style={{
                  backgroundColor: '#1a237e',
                  color: 'white'
                }}>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '240px'
                  }}>
                    {getMarketGroupColumnLabel(resolveDashboardGrouping('ltip'))}
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '160px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: '8px'
                    }}>
                      Надежность данных
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsReliabilityModalOpen(true);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: 'white',
                          padding: '0',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="fas fa-question-circle"></i>
                      </button>
                    </div>
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '120px'
                  }}>
                    Кол-во датакейсов
                  </th>
                  <th style={{ 
                    padding: '16px 12px', 
                    textAlign: 'center', 
                    fontWeight: '600',
                    fontSize: '16px',
                    width: '300px',
                    whiteSpace: 'nowrap',
                  }}>
                    Да / Нет
                  </th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Получаем уникальные позиции из отфильтрованных данных
                  const marketGroups = getDashboardMarketGroups('ltip');
                  
                  return marketGroups.map((group, index) => {
                    const positionData = getDashboardGroupRecords('ltip', group.key);
                    
                    // Анализируем данные ltip
                    const ltipYesCount = positionData.filter(record => record.ltip && record.ltip.toLowerCase() === 'да').length;
                    const ltipNoCount = positionData.filter(record => !record.ltip || record.ltip.toLowerCase() === 'нет').length;
                    const totalLtipCount = ltipYesCount + ltipNoCount;
                    
                    // Вычисляем проценты
                    const ltipYesPercentage = totalLtipCount > 0 ? (ltipYesCount / totalLtipCount) * 100 : 0;
                    const ltipNoPercentage = totalLtipCount > 0 ? (ltipNoCount / totalLtipCount) * 100 : 0;
                    
                    const reliabilityValues = positionData.map(record => record.reliability_score);
                    
                    // Вычисляем среднюю надежность
                    const avgReliability = reliabilityValues.length > 0 
                      ? reliabilityValues.reduce((sum, val) => sum + val, 0) / reliabilityValues.length
                      : 0;

                    // Функция для определения уровня надежности
                    const getReliabilityLevel = (score: number) => {
                      if (score > 0.8) {
                        return { 
                          level: 'Excellent', 
                          color: '#073763', 
                          text: 'Очень высокая достоверность (Excellent)',
                          shortText: 'Excellent'
                        };
                      } else if (score > 0.5) {
                        return { 
                          level: 'High', 
                          color: '#1155cc', 
                          text: 'Высокая достоверность (High)',
                          shortText: 'High'
                        };
                      } else if (score > 0.3) {
                        return { 
                          level: 'Moderate', 
                          color: '#6d9eeb', 
                          text: 'Умеренная достоверность (Moderate)',
                          shortText: 'Moderate'
                        };
                      } else {
                        return { 
                          level: 'Low', 
                          color: '#999999', 
                          text: 'Информация требует проверки (Low)',
                          shortText: 'Low'
                        };
                      }
                    };

                    const reliabilityLevel = getReliabilityLevel(avgReliability);
                    
                    return (
                      <tr 
                        key={group.key} 
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e3f2fd';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#fafafa';
                        }}
                      >
                        <td style={{ 
                          padding: '12px 12px',
                          fontWeight: '500',
                          color: '#333'
                        }}>
                          <MarketGroupLabelCell group={group} />
                        </td>
                        <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}>
                            <div style={{
                              width: '16px',
                              height: '16px',
                              backgroundColor: reliabilityLevel.color,
                              borderRadius: '3px',
                              border: '1px solid #e0e0e0'
                            }}></div>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: '#333'
                            }}>
                              {reliabilityLevel.shortText}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 12px', textAlign: 'center' }}>
                          {positionData.length}
                        </td>
                        <td style={{ 
                          padding: '12px 12px', 
                          textAlign: 'center'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}>
                            {/* Индикатор-линейка */}
                            <div style={{
                              width: '160px',
                              height: '20px',
                              backgroundColor: '#e3f2fd',
                              borderRadius: '10px',
                              overflow: 'hidden',
                              position: 'relative',
                              border: '1px solid #bbdefb'
                            }}>
                              {/* Зеленая часть (ltip = "да") */}
                              <div style={{
                                position: 'absolute',
                                left: '0',
                                top: '0',
                                height: '100%',
                                width: `${ltipYesPercentage}%`,
                                backgroundColor: '#4caf50',
                                transition: 'width 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {ltipYesPercentage > 15 && (
                                  <span style={{
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: 'white',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                  }}>
                                    Есть
                                  </span>
                                )}
                              </div>
                              {/* Бледно-синяя часть (ltip = "нет") */}
                              <div style={{
                                position: 'absolute',
                                right: '0',
                                top: '0',
                                height: '100%',
                                width: `${ltipNoPercentage}%`,
                                backgroundColor: '#6d9eeb',
                                transition: 'width 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {ltipNoPercentage > 15 && (
                                  <span style={{
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: 'white',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                  }}>
                                    Нет
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Текст с процентами */}
                            <div style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: '#333',
                              minWidth: '72px',
                              textAlign: 'left'
                            }}>
                              {ltipYesPercentage.toFixed(0)}% / {ltipNoPercentage.toFixed(0)}%
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
              </table>
          </div>
          </div>
        </div>
      )}

      {/* Дэшборд «Форма LTIP» */}
      {filteredData.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <div style={{ width: 'fit-content', maxWidth: '100%', margin: '0 auto' }}>
          <MarketDashboardSectionHeader
            title="Форма LTIP"
            groupingMode={resolveDashboardGrouping('ltipForm')}
            onGroupingModeChange={(mode) => setDashboardGroupingMode('ltipForm', mode)}
            subtitle="Распределение форм LTIP среди записей, где долгосрочная мотивация предусмотрена"
          />

          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            overflowX: 'auto',
            overflowY: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            width: 'fit-content',
            maxWidth: '100%',
          }}>
            <table style={{
              width: 'auto',
              borderCollapse: 'collapse',
              fontSize: '14px',
              tableLayout: 'fixed',
            }}>
              <thead>
                <tr style={{ backgroundColor: '#1a237e', color: 'white' }}>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '220px',
                  }}>
                    {getMarketGroupColumnLabel(resolveDashboardGrouping('ltipForm'))}
                  </th>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '130px',
                    whiteSpace: 'nowrap',
                  }}>
                    Надежность данных
                  </th>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '100px',
                    whiteSpace: 'nowrap',
                  }}>
                    Кол-во датакейсов
                  </th>
                  <th style={{
                    padding: '16px 12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    fontSize: '16px',
                    width: '400px',
                  }}>
                    Распределение форм LTIP
                  </th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const marketGroups = getDashboardMarketGroups('ltipForm');

                  return marketGroups.map((group, index) => {
                    const positionData = getDashboardGroupRecords('ltipForm', group.key);
                    const ltipRecordsCount = countRecordsWithLtipForm(positionData);
                    const formStats = computeLtipFormStats(positionData);

                    const reliabilityValues = positionData.map((record) => record.reliability_score);
                    const avgReliability = reliabilityValues.length > 0
                      ? reliabilityValues.reduce((sum, val) => sum + val, 0) / reliabilityValues.length
                      : 0;

                    const getReliabilityLevel = (score: number) => {
                      if (score > 0.8) return { color: '#073763', shortText: 'Excellent' };
                      if (score > 0.5) return { color: '#1155cc', shortText: 'High' };
                      if (score > 0.3) return { color: '#6d9eeb', shortText: 'Moderate' };
                      return { color: '#999999', shortText: 'Low' };
                    };

                    const reliabilityLevel = getReliabilityLevel(avgReliability);

                    return (
                      <tr
                        key={`ltip-form-${group.key}`}
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                          transition: 'background-color 0.2s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e3f2fd'; }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#fafafa';
                        }}
                      >
                        <td style={{ padding: '14px 12px', fontWeight: '500', color: '#333', verticalAlign: 'middle' }}>
                          <MarketGroupLabelCell group={group} />
                        </td>
                        <td style={{ padding: '14px 12px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <div style={{
                              width: '16px',
                              height: '16px',
                              backgroundColor: reliabilityLevel.color,
                              borderRadius: '3px',
                              border: '1px solid #e0e0e0',
                            }} />
                            <span style={{ fontSize: '12px', fontWeight: '500', color: '#333' }}>
                              {reliabilityLevel.shortText}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '14px 12px', textAlign: 'center', verticalAlign: 'middle' }}>
                          {ltipRecordsCount}
                        </td>
                        <td style={{ padding: '14px 16px', verticalAlign: 'middle', textAlign: 'left' }}>
                          <LtipFormDistributionChart stats={formStats} />
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <DataPackagesModal 
          onClose={handleCloseModal} 
          onPackageSelect={handlePackageSelect}
        />
      )}

      <ReliabilityModal
        isOpen={isReliabilityModalOpen}
        onClose={() => setIsReliabilityModalOpen(false)}
      />

      <DataPackageModal
        dataPackage={detailPackage}
        isOpen={isDetailModalOpen}
        onClose={() => { setIsDetailModalOpen(false); setDetailPackage(null); }}
        hasAccess={detailPackage ? (catalogPackages.find(p => p.id === detailPackage.id)?.hasAccess ?? false) : false}
        onOpen={() => {
          if (detailPackage?.package_name) {
            setIsDetailModalOpen(false);
            if (onOpenPackage) {
              onOpenPackage(detailPackage.package_name, detailPackage.name);
            } else {
              handlePackageSelect(detailPackage.package_name, detailPackage.name);
            }
          }
        }}
      />

      <AccessDeniedModal
        isOpen={accessDeniedModal.isOpen}
        onClose={() => setAccessDeniedModal({ isOpen: false, packageName: '' })}
        packageName={accessDeniedModal.packageName}
        dataPackageId={accessDeniedModal.dataPackageId}
        context="Обзор рынка"
      />
    </div>
  );
};

const CatalogStat: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666' }}>
    <i className={`fas ${icon}`} aria-hidden="true" style={{ color: '#9fa8da', width: '14px', textAlign: 'center' }} />
    <span>{label}</span>
  </div>
);

interface IndustrySelectorProps {
  value: string;
  onChange: (id: string) => void;
  options: { id: string; label: string }[];
  optionsLoading?: boolean;
  compact?: boolean;
}

const IndustrySelector: React.FC<IndustrySelectorProps> = ({
  value,
  onChange,
  options,
  optionsLoading = false,
  compact = false,
}) => {
  const placeholder = optionsLoading ? 'Загрузка индустрий...' : 'Выберите индустрию...';

  return (
    <div className={`market-industry-panel${compact ? ' market-industry-panel--compact' : ''}`} data-tour="market-industry">
      <span className="market-industry-panel__label" id="industry-select-label">
        <i className="fas fa-industry" aria-hidden="true" />
        Индустрия
      </span>
      <CustomSelect
        id="industry-select"
        ariaLabelledBy="industry-select-label"
        value={value}
        onChange={onChange}
        options={options.map((item) => ({ value: item.id, label: item.label }))}
        placeholder={placeholder}
        disabled={optionsLoading}
        className="market-industry-panel__picker"
        triggerClassName="market-industry-panel__select"
        menuClassName="market-industry-panel__menu"
        optionClassName="market-industry-panel__option"
      />
    </div>
  );
};

export default MarketSection;
